import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Tabs,
  Tab,
  Button,
  Typography,
  Select,
  MenuItem,
  useMediaQuery,
  useTheme,
  CircularProgress,
} from '@mui/material';
import { useStyles } from '../styles';
import { ReactComponent as SortIcon } from 'assets/icons/SortIcon.svg';
import { ReactComponent as FilterIcon } from 'assets/icons/Filter.svg';
import { DropdownIcon } from 'assets/svgComponents/DropdownIcon';
import { SortMenu } from './SortMenu';
import { FilterMenu } from './FilterMenu';
import { SortType, CertificationCounts, FilterState } from '../types';
import PlusCertificateIcon from 'assets/svgComponents/PlusCertificateIcon';
import { STYLE } from 'constants/style';

interface Project {
  id: string;
  name: string;
}

interface CertificationTabsProps {
  activeTab: number;
  onTabChange: (event: React.SyntheticEvent, newValue: number) => void;
  certificationCounts: CertificationCounts;
  onSortChange?: (sortType: SortType) => void;
  sortType?: SortType;
  onFilterChange?: (filterState: FilterState) => void;
  filterState?: FilterState;
  onAddCertification?: () => void;
  projects?: Project[];
  isLoadingProjects?: boolean;
}

export const CertificationTabs: React.FC<CertificationTabsProps> = ({
  activeTab,
  onTabChange,
  certificationCounts,
  onSortChange,
  sortType,
  onFilterChange,
  filterState,
  onAddCertification,
  projects = [],
  isLoadingProjects = false,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sortAnchorEl, setSortAnchorEl] = useState<null | HTMLElement>(null);
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);

  const createTabLabel = (text: string, count: number) => (
    <Box className={classes.customTabLabel}>
      <Typography component="span">{text}</Typography>
      <Box className={`${classes.customCountBadge} custom-count-badge`}>{count < 10 ? `0${count}` : count}</Box>
    </Box>
  );

  const handleSortClick = (event: React.MouseEvent<HTMLElement>) => {
    setSortAnchorEl(event.currentTarget);
  };

  const handleSortClose = () => {
    setSortAnchorEl(null);
  };

  const handleSortChange = (sortType: SortType) => {
    if (onSortChange) {
      onSortChange(sortType);
    }
  };

  const handleFilterClick = (event: React.MouseEvent<HTMLElement>) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const handleFilterChange = (filterState: FilterState) => {
    if (onFilterChange) {
      onFilterChange(filterState);
    }
  };

  const handleDropdownChange = (event: any) => {
    onTabChange(event, event.target.value);
  };

  const tabOptions = [
    {
      value: 0,
      key: 'required',
      label: t('myCertifications.tabs.required'),
      count: certificationCounts.required,
    },
    {
      value: 1,
      key: 'expired',
      label: t('myCertifications.tabs.expired'),
      count: certificationCounts.expired,
    },
    {
      value: 2,
      key: 'current',
      label: t('myCertifications.tabs.current'),
      count: certificationCounts.current,
    },
  ];

  return (
    <Box className={classes.tabsContainer} data-testid="my-certifications-tabs-container">
      {isMobile ? (
        <Select
          value={activeTab}
          onChange={handleDropdownChange}
          className={classes.tabDropdown}
          data-testid="my-certifications-tab-dropdown"
          IconComponent={DropdownIcon}
          renderValue={value => {
            const selected = tabOptions.find(option => option.value === value);
            return <Typography component="span">{selected?.label}</Typography>;
          }}
        >
          {tabOptions.map(option => (
            <MenuItem
              key={option.value}
              value={option.value}
              data-testid={`my-certifications-tab-dropdown-item-${option.key}`}
              className={classes.dropdownMenuItem}
            >
              <Typography component="span">{option.label}</Typography>
            </MenuItem>
          ))}
        </Select>
      ) : (
        <Tabs value={activeTab} onChange={onTabChange} className={classes.tabs} data-testid="my-certifications-tabs">
          <Tab
            label={createTabLabel(t('myCertifications.tabs.required'), certificationCounts.required)}
            className={classes.tab}
            data-testid="my-certifications-tab-required"
          />
          <Tab
            label={createTabLabel(t('myCertifications.tabs.expired'), certificationCounts.expired)}
            className={classes.tab}
            data-testid="my-certifications-tab-expired"
          />
          <Tab
            label={createTabLabel(t('myCertifications.tabs.current'), certificationCounts.current)}
            className={classes.tab}
            data-testid="my-certifications-tab-current"
          />
        </Tabs>
      )}
      <Box className={classes.actionButtons}>
        {!isMobile ? (
          <Button
            startIcon={<PlusCertificateIcon color={STYLE.COLOR.WHITE} />}
            className={classes.addButton}
            data-testid="my-certifications-add-button"
            onClick={onAddCertification}
          >
            {t('myCertifications.actions.addCertification')}
          </Button>
        ) : null}
        <Button
          startIcon={isMobile ? undefined : <SortIcon />}
          className={classes.sortButton}
          data-testid="my-certifications-sort-button"
          onClick={handleSortClick}
        >
          {isMobile ? <SortIcon /> : t('myCertifications.actions.sort')}
        </Button>
        <Button
          startIcon={isMobile ? undefined : isLoadingProjects ? <CircularProgress size={16} /> : <FilterIcon />}
          className={classes.filterButton}
          data-testid="my-certifications-filter-button"
          onClick={handleFilterClick}
          disabled={isLoadingProjects}
        >
          {isMobile ? (
            isLoadingProjects ? (
              <CircularProgress size={16} />
            ) : (
              <FilterIcon />
            )
          ) : (
            t('myCertifications.actions.filter')
          )}
        </Button>
        {isMobile ? (
          <Button className={classes.addButton} data-testid="my-certifications-add-button" onClick={onAddCertification}>
            <PlusCertificateIcon color={STYLE.COLOR.WHITE} />
          </Button>
        ) : null}
      </Box>

      <SortMenu
        anchorEl={sortAnchorEl}
        onClose={handleSortClose}
        onSortChange={handleSortChange}
        currentSort={sortType}
        activeTab={activeTab}
      />
      <FilterMenu
        anchorEl={filterAnchorEl}
        onClose={handleFilterClose}
        onFilterChange={handleFilterChange}
        filterState={filterState}
        projects={projects}
        activeTab={activeTab}
      />
    </Box>
  );
};
